import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges, OnInit,
  Output,
  SimpleChanges, ViewChild
} from '@angular/core';

import {TranslocoPipe} from '@jsverse/transloco';
import {PHOTOGRAPHY_TAGS, PhotographyTag} from '../../../core/entities/photography/photography-tag.entity';
import {Sheet} from '../../../core/components/sheet/sheet';
import {FormsModule} from '@angular/forms';
import {GeoService} from '../../../creator/services/geo/geo.service';
import {debounceTime, distinctUntilChanged, of, Subject, switchMap} from 'rxjs';
@Component({
  selector: 'app-search-banner',
  imports: [
    TranslocoPipe,
    Sheet,
    FormsModule
  ],
  templateUrl: './search-banner.component.html',
  styleUrl: './search-banner.component.scss'
})
export class SearchBannerComponent implements OnInit, OnChanges {
  @Input() city: string|null = null;
  @Input() tags: string[]|null = null;
  @Output() onCityChange = new EventEmitter<string|null>();
  @Output() onTagsChange = new EventEmitter<string[]>();

  public popularCities = ['Paris', 'Marseille', 'Bordeaux', 'Lille', 'Toulouse'];
  public selectedCity: string|null = null;
  public cityListIsOpened = false;
  public filteredCities: string[] = [];

  public allTags = PHOTOGRAPHY_TAGS;
  public selectedTags: PhotographyTag[]|null = null;
  public tagsListIsOpened = false;

  public tagSearchQuery = '';
  public citySearchQuery = '';
  @ViewChild('tagInput') tagInputRef!: ElementRef<HTMLInputElement>;
  @ViewChild('cityInput') cityInputRef!: ElementRef<HTMLInputElement>;
  private citySearchSubject = new Subject<string>();
  public citySearchLoading = false;

  public mobileSearchOpen = false;

  constructor(
    private readonly _eref: ElementRef,
    private readonly geoService: GeoService,
  ) {
  }

  @HostListener('document:click', ['$event'])
  onClickOutside(event: MouseEvent) {
    if (!this._eref.nativeElement.contains(event.target)) {
      this.cityListIsOpened = false;
      this.tagsListIsOpened = false;
    }
  }

  ngOnInit(): void {
    this.filteredCities = this.popularCities;

    this.citySearchSubject
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        switchMap((query) => {
          this.citySearchLoading = true;
          if (!query || query.trim() === '') {
            this.filteredCities = this.popularCities;
            return of(this.filteredCities);
          }
          return this.geoService.searchCities(query);
        })
      )
      .subscribe((cities: string[]) => {
        if (cities.length > 0) {
          this.filteredCities = cities;
        }
        this.citySearchLoading = false;
      });
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['city'] && this.city) {
      this.selectedCity = this.city.replace(/\w\S*/g, (txt) => {
        return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
      });
    }
    if (changes['tags'] && this.tags) {
      this.selectedTags = this.allTags.filter(t => this.tags!.includes(t.label));
    }
  }

  public toggleTagsList(): void {
    this.tagsListIsOpened = !this.tagsListIsOpened;
    this.cityListIsOpened = false;

    if (this.tagsListIsOpened) {
      setTimeout(() => this.tagInputRef?.nativeElement?.focus(), 0);
    }
  }

  public toggleCityList(): void {
    this.cityListIsOpened = !this.cityListIsOpened;
    this.tagsListIsOpened = false;

    if (this.cityListIsOpened) {
      setTimeout(() => this.cityInputRef?.nativeElement?.focus(), 0);
    }
  }

  public selectCity(city: string|null) {
    this.citySearchQuery = '';
    if (city !== null) {
      this.selectedCity = city;
      this.cityListIsOpened = false;
      this.onCityChange.emit(this.getCitySlug(city));
    } else {
      this.selectedCity = null;
      this.cityListIsOpened = false;
      this.onCityChange.emit(null);
    }
  }

  public selectTag(tag: PhotographyTag|null): void {
    this.tagSearchQuery = '';
    if (tag !== null) {
      this.selectedTags = [tag];
      this.tagsListIsOpened = false;
      this.onTagsChange.emit([tag.label]);
    } else {
      this.selectedTags = null;
      this.tagsListIsOpened = false;
      this.onTagsChange.emit([]);
    }
  }

  public openMobileSearch(): void {
    this.mobileSearchOpen = true;
  }

  public closeMobileSearch(): void {
    this.mobileSearchOpen = false;
    this.tagsListIsOpened = false;
    this.cityListIsOpened = false;
  }

  get filteredTags() {
    return this.allTags.filter(tag =>
      tag.label.toLowerCase().includes(this.tagSearchQuery.toLowerCase())
    );
  }

  private getCitySlug(city: string): string {
    return city.trim().toLowerCase().replace(/\s+/g, '-');
  }

  public onCitySearchQueryChanged(query: string): void {
    this.citySearchSubject.next(query);
  }
}
